/**
 * The rehearsal bar's arithmetic, with no React and no fetch in it (TST-003).
 *
 * Everything the strip decides - what the robo baker should press next, whether
 * Play still has anywhere to go, how far ahead of wall time the platform has
 * been pushed - is a function of the last `/dev/rehearsal` answer and nothing
 * else. Kept here, separately, because `packages/ui` ships no DOM test
 * environment (see `tokens.test.ts`): a decision written inside the component
 * is a decision no test in this repository can reach.
 */

/** One order the cockpit can act on, exactly as `/dev/rehearsal` sends it. */
export interface RehearsalOrder {
  readonly orderId: string;
  readonly publicId: string | null;
  readonly status: string;
  /**
   * The shared delivery-phase classification
   * (`packages/contracts/src/projections/delivery-phase.ts`), derived by the
   * WORKER and sent on the wire.
   *
   * Deliberately not re-derived here. `packages/ui` depends on React and
   * nothing else, and a status list copied into a design package is the exact
   * shape of defect that projections directory exists to close - two places
   * classifying one status, disagreeing a few rows apart on the same screen.
   */
  readonly phase: string;
  readonly serviceDate: string;
  readonly promisedWindowStart: string;
  readonly bakeryLocationId: string | null;
}

export interface RehearsalClockView {
  readonly now: string;
  readonly wallNow: string;
  readonly offsetMs: number;
  readonly offsetMinutes: number;
}

export interface RehearsalSimulatorView {
  readonly simulatedNow: string;
  readonly nextEventAt: string | null;
  readonly jobs: number;
  readonly scenarios: readonly string[];
}

/** The whole cockpit read. */
export interface RehearsalSnapshot {
  readonly clock: RehearsalClockView;
  readonly provider: string;
  readonly simulator: RehearsalSimulatorView | null;
  readonly parisServiceDay: string;
  readonly ordersInFlight: readonly RehearsalOrder[];
  readonly robo: { readonly baker: boolean };
}

export type RoboAction = 'ready' | 'collected';

export interface RoboPress {
  readonly orderId: string;
  readonly action: RoboAction;
  /** Stable per order and action: what a bar remembers it has already sent. */
  readonly key: string;
}

export function roboPressKey(orderId: string, action: RoboAction): string {
  return `${orderId}:${action}`;
}

/** How long Play waits between steps. */
export const PLAY_INTERVAL_MS = 3_000;

/** How often the bar re-reads the cockpit. */
export const POLL_INTERVAL_MS = 2_000;

/**
 * What the robo baker should press right now.
 *
 * Two taps, in the order a counter makes them: the bag when the order is being
 * prepared, the hand-over when a courier is engaged on it. Both questions are
 * asked of facts the WORKER classified - `status` and the shared `phase` - so
 * this never grows a status list of its own.
 *
 * `alreadySent` is what stops one tab pressing twice while a press is in
 * flight. It is NOT what stops two tabs pressing at once: they are four
 * different origins and share nothing, so that guarantee is the server's, which
 * answers the second press `applied: false` rather than failing it.
 */
export function roboBakerPresses(
  snapshot: RehearsalSnapshot | null,
  alreadySent: ReadonlySet<string>,
): readonly RoboPress[] {
  if (snapshot === null || !snapshot.robo.baker) return [];
  const presses: RoboPress[] = [];
  for (const order of snapshot.ordersInFlight) {
    const action: RoboAction | null =
      order.status === 'PREPARING' ? 'ready' : order.phase === 'EN_ROUTE' ? 'collected' : null;
    if (action === null) continue;
    const key = roboPressKey(order.orderId, action);
    if (alreadySent.has(key)) continue;
    presses.push({ orderId: order.orderId, action, key });
  }
  return presses;
}

/** True while the simulated courier still has an event to be stepped to. */
export function playContinues(snapshot: RehearsalSnapshot | null): boolean {
  return snapshot?.simulator?.nextEventAt != null;
}

/**
 * The orders a scenario can still be bound to.
 *
 * The mock provider lays a job's whole timeline out when it CREATES the job, so
 * `POST /dev/simulator/scenario` has to be sent before the order dispatches -
 * choosing one afterwards changes nothing and looks like a bug in the
 * simulator. Offering the select only where it can still take effect is how the
 * strip says so without a sentence of explanation.
 */
export function scenarioTargets(snapshot: RehearsalSnapshot | null): readonly RehearsalOrder[] {
  if (snapshot === null || snapshot.simulator === null) return [];
  return snapshot.ordersInFlight.filter((order) => order.phase === 'NOT_DISPATCHED');
}

/** A short handle for an order, for a strip with no room for a uuid. */
export function orderHandle(order: RehearsalOrder): string {
  const source = order.publicId ?? order.orderId;
  return source.length <= 8 ? source : source.slice(-8);
}

/**
 * How far ahead of wall time the rehearsal stands, in words.
 *
 * `live` at zero rather than `+0m`: the difference between "this platform is
 * running on real time" and "this platform has been pushed" is the one thing
 * the badge exists to say, and a zero that looks like every other reading says
 * it badly.
 */
export function formatOffset(offsetMs: number): string {
  const totalMinutes = Math.round(offsetMs / 60_000);
  if (totalMinutes <= 0) return 'live';
  const days = Math.floor(totalMinutes / 1_440);
  const hours = Math.floor((totalMinutes % 1_440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  // Minutes are dropped only once the offset is measured in days AND hours -
  // "+2d 3h 0m" reads as precision the rehearsal does not have.
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
  return `+${parts.join(' ')}`;
}

/**
 * The platform's own clock, as Paris reads it.
 *
 * `en-GB` on purpose: Paris reads a 24-hour clock, and `en-US` here would print
 * a 12-hour one. This is a FORMATTER, not a displayed string, so the American
 * English rule does not reach it (CLAUDE.md).
 */
export function formatPlatformTime(iso: string, timeZone = 'Europe/Paris'): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '--:--';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(at);
}

/** The next courier event's wall time, or a dash when nothing is scheduled. */
export function formatNextEvent(snapshot: RehearsalSnapshot | null): string {
  const at = snapshot?.simulator?.nextEventAt ?? null;
  return at === null ? '--:--' : formatPlatformTime(at);
}
